import {
  type DoczBlock,
  type PaletteBlock,
  parseBlockAddress,
  type StructuredBlock,
} from "@norkron/web-docz";
import {
  canvasDoczBlocks,
  canvasInsertStructuredAfter,
  type CanvasEditorHandle,
} from "@norkron/web-page-layout";
import type { BlockPatch, InsertItem } from "@norkron/web-elaborate-editor";

/**
 * `canvasInsertLayer` — the CANVAS structural-insert model.
 *
 * ## What was broken
 *
 * `<CanvasElaborateEditor>` published `insertBlock: () => false` and
 * `insertPageBreak: () => null`, and `canvasCapabilities.ts` declared the whole
 * group `available: false` with "STUB: no canvas insert model yet (TODO C2/C4) —
 * every verb declines". Every `requires: 'blockPalette'` command therefore rendered
 * disabled — except `insert.page-break`, which has no `requires` gate at all and so
 * rendered ENABLED and silently dead.
 *
 * ## What this does, and what it REFUSES — every line of it measured
 *
 * A structural insert is: mint an id, place the STRUCTURED block after the caret's
 * top-level block through `@norkron/web-page-layout`'s `canvasInsertStructuredAfter`
 * (which rebuilds the docz handle in `docz.wasm`), install the produced document as
 * ONE undoable step via `EditorCore.applyBridge`, and put the caret in the first
 * addressable region of what was just inserted. No block bytes are framed here.
 *
 * The refusals below are NOT conservatism. Each was measured against the real
 * `docz.wasm` + `pdfz-layout.wasm`, and each names a concrete upstream gap:
 *
 *   · **`image` / `imageSvg` — REFUSED.** `FormatApi.insertImage()` emits
 *     `{srcKind:'path'}`, and `pl_layout` does not degrade on an unresolvable path:
 *     it returns `LAYOUT` and the WHOLE DOCUMENT fails to lay out. One image insert
 *     would blank the canvas. The `srcKind:'bytes'` alternative cannot carry a
 *     payload from TypeScript either — `block-dto.ts` writes `w.str(block.src)` as
 *     UTF-8, so a byte string is mangled on the wire (measured: `0x89` arrives as
 *     `0xc2 0x89`; the rendered `<img src>` begins `wolQTkcN` where the source was
 *     `iVBORw0`). Refusing is the only non-destructive answer. FR filed.
 *   · **`pageBreak` — REFUSED**, see {@link CanvasInsertLayer.insertPageBreak}.
 *   · **`table` / `horizontalRule` / everything else — REAL.** An inserted 3x3
 *     table projects nine caret-addressable `table_cell` blocks with correct
 *     sub-block addresses, typing lands in the right cell, `toHtml()` carries it,
 *     and the structural census of the posted bytes shows ZERO loss — so it also
 *     cannot trip the save-refusal predicate.
 *
 * A refusal is REPORTED, never silent: it goes to the same `onRefusal` channel the
 * hit-test refusal uses, which `<DoczCanvas>` renders as a toast.
 */

export interface CanvasInsertLayerDeps {
  /** The live canvas editor handle (`null` before mount / after dispose). */
  getEditor(): CanvasEditorHandle | null;
  /** Announce a refusal in words the reviewer can read (the toast channel). */
  refuse(message: string): void;
}

export interface CanvasInsertLayer {
  /** Insert a full-palette block after the caret's top-level block. `false` when
   *  nothing moved — never a fabricated success. */
  insertBlock(block: PaletteBlock): boolean;
  /** Always `null` on the canvas, and it SAYS SO. See the doc comment. */
  insertPageBreak(): BlockPatch | null;
  /** The content-model insert menu. Empty on both surfaces — the docz content
   *  model has no WASM export for it yet (upstream scaffold, `types.ts`). */
  getInsertMenu(): InsertItem[];
}

/** Palette kinds the canvas cannot insert, and the reason a reviewer is told. */
const REFUSED: Readonly<Record<string, string>> = {
  image:
    "Pictures can't be added in the page view yet. Switch to the continuous view to add one.",
  imageSvg:
    "Pictures can't be added in the page view yet. Switch to the continuous view to add one.",
  pageBreak:
    "Page breaks aren't supported by this document engine yet — the layout pass ignores them and they wouldn't survive a save.",
};

let insertSeq = 0;
/** A collision-free id for an inserted block. Opaque to web-docz; a save-back
 *  re-carves the resource, so these are edit-session handles, never persisted keys. */
function freshBlockId(): string {
  insertSeq += 1;
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `ins-${Date.now().toString(36)}-${insertSeq}-${rand}`;
}

export function createCanvasInsertLayer(deps: CanvasInsertLayerDeps): CanvasInsertLayer {
  return {
    insertBlock(block: PaletteBlock): boolean {
      const editor = deps.getEditor();
      if (!editor) return false;
      const refusal = REFUSED[block.kind];
      if (refusal) {
        deps.refuse(refusal);
        return false;
      }
      const { bridge, selection } = editor.core.getState();
      // AN INSERTED BLOCK MUST CARRY AN ID. A table with none projects id-LESS
      // cells, and an id-less cell is unroutable: the caret would paint into a
      // table nobody can type in. Minting here (rather than trusting the caller)
      // is what makes the very first click into a fresh table work.
      const id = (block as { id?: string }).id ?? freshBlockId();
      const next = canvasInsertStructuredAfter(bridge, selection.focus.blockOrd, {
        ...block,
        id,
      } as StructuredBlock);
      if (!next || next === bridge) return false;
      const ord = firstAddressableOrd(next, id);
      const at = { blockOrd: ord, byteOffset: 0, affinity: "downstream" as const };
      editor.core.applyBridge(next, { selection: { anchor: at, focus: at } });
      return true;
    },

    /**
     * A page break DECLINES on this surface, visibly.
     *
     * MEASURED, not assumed: a structured `{kind:'pageBreak'}` block laid out
     * through the real `pl_layout` leaves the page count UNCHANGED whether it sits
     * mid-document or at the end, and `handle.toHtml()` renders it as the EMPTY
     * STRING — so it would neither break a page nor survive save + reload. Both
     * halves are missing upstream (the pdfz layout pass and docz's `html_writer`);
     * FRs are filed. Inserting it anyway would be a fabricated success: the
     * reviewer would see nothing happen and the break would vanish on reload.
     *
     * The command is ALSO gated off via the `pageBreak` capability, so it renders
     * DISABLED with the reason rather than reaching here at all. This refusal is
     * the honest floor beneath that gate, for a host that calls the verb directly.
     */
    insertPageBreak(): BlockPatch | null {
      deps.refuse(REFUSED.pageBreak as string);
      return null;
    },

    getInsertMenu(): InsertItem[] {
      return [];
    },
  };
}

/** The document ordinal of the first projected block belonging to `rootId` — a
 *  table's first CELL, or the block itself. Falls back to the document start when
 *  the inserted block projects nothing addressable (a horizontal rule). */
function firstAddressableOrd(
  bridge: Parameters<typeof canvasDoczBlocks>[0],
  rootId: string,
): number {
  const blocks: readonly DoczBlock[] | null = canvasDoczBlocks(bridge);
  if (!blocks) return 0;
  const i = blocks.findIndex(
    (b) => b.id != null && parseBlockAddress(b.id).rootId === rootId,
  );
  return i < 0 ? 0 : i;
}
